import { useEffect, useState, useCallback, useRef } from 'react'

const API_BASE = 'https://www.medicinacondante.com/cursos.php'

// ─────────────────────────────────────────────────────
// Detectar tráfico desde Meta Ads
// ─────────────────────────────────────────────────────

function userComesFromAd() {
  const params = new URLSearchParams(window.location.search)
  if (params.has('fbclid')) return true
  if (params.has('fbc')) return true
  const cookies = document.cookie.split(';').map((c) => c.trim())
  const fbcCookie = cookies.find((c) => c.startsWith('_fbc='))
  if (fbcCookie) return true
  return false
}

// ─────────────────────────────────────────────────────
// Inyectar pixel base
// ─────────────────────────────────────────────────────

function injectPixelBase(pixelId) {
  if (!pixelId) return

  const existingId = `meta-pixel-base-${pixelId}`
  if (document.getElementById(existingId)) return

  const script = document.createElement('script')
  script.id = existingId
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;
    n.push=n;
    n.loaded=!0;
    n.version='2.0';
    n.queue=[];
    t=b.createElement(e);
    t.async=!0;
    t.src=v;
    s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}
    (window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');

    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `
  document.head.appendChild(script)
}

// ─────────────────────────────────────────────────────
// Inicializar pixel extra (individual por curso)
// ─────────────────────────────────────────────────────

function initExtraPixel(pixelId) {
  if (!pixelId || typeof window.fbq !== 'function') return
  window.fbq('init', pixelId)
}

// ─────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────

export function useMetaPixel() {

  const [globalPixelId,  setGlobalPixelId]  = useState(null)

  // useGlobalPixel refleja pixel_global de BD
  const [useGlobalPixel, setUseGlobalPixel] = useState(true)

  // pageViewActivo refleja el campo "estado" de BD:
  //   true  → estado = 1 (el <head> disparó PageView)
  //   false → estado = 0 (el <head> NO cargó el pixel)
  const [pageViewActivo, setPageViewActivo] = useState(true)

  const [fromAd,         setFromAd]         = useState(false)
  const [initialized,    setInitialized]    = useState(false)

  const initializedPixels = useRef(new Set())

  // ─────────────────────────────────────────────────────
  // Cargar configuración desde pixel_meta
  // Lee pixel_id      → para inyectar el script base si estado = 1
  // Lee pixel_global  → para decidir qué pixel usar en eventos de compra
  // Lee estado        → para saber si PageView fue disparado en el <head>
  // ─────────────────────────────────────────────────────

  useEffect(() => {

    setFromAd(userComesFromAd())

    fetch(`${API_BASE}?recurso=pixel`)
      .then((r) => r.json())
      .then((data) => {

        if (!Array.isArray(data) || !data.length) return

        const registro = data[0]

        if (!registro?.pixel_id) return

        const pid       = registro.pixel_id
        const esGlobal  = Number(registro.pixel_global) === 1
        // estado: 1 = PageView activo | 0 = desactivado
        const esActivo  = Number(registro.estado) === 1

        setGlobalPixelId(pid)
        setUseGlobalPixel(esGlobal)
        setPageViewActivo(esActivo)

        // Solo inyectamos el pixel base si estado = 1
        // (el index.html ya lo hizo si estado=1, pero como respaldo
        //  para cuando el hook se usa sin el script del <head>)
        if (esActivo) {
          injectPixelBase(pid)
          initializedPixels.current.add(pid)
        }

        setInitialized(true)

        if (process.env.NODE_ENV === 'development') {
          console.log('[MetaPixel] Configuración cargada desde BD:', {
            pixel_id:     pid,
            pixel_global: esGlobal,
            estado:       esActivo,
            modo:         esGlobal ? 'GLOBAL' : 'INDIVIDUAL',
            pageview:     esActivo ? 'ACTIVO' : 'INACTIVO',
          })
        }
      })
      .catch(() => {})

  }, [])

  // ─────────────────────────────────────────────────────
  // Evento de checkout / compra
  //
  //  Si pixel_global = true  → fbq('track', ...)       con el pixel global
  //  Si pixel_global = false → fbq('trackSingle', ...) con el pixel del curso
  //
  //  Nota: el evento Purchase NO depende de "estado" (ese solo
  //  controla el PageView del <head>). Purchase sigue las reglas
  //  de pixel_global y fromAd.
  // ─────────────────────────────────────────────────────

  const fireInitiateCheckout = useCallback(
    (curso) => {

      if (!fromAd) return
      if (typeof window.fbq !== 'function') return

      const cursoPixel = curso?.pixel?.trim()

      const targetPixel = useGlobalPixel
        ? globalPixelId
        : cursoPixel

      if (!targetPixel) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[MetaPixel] Sin pixel destino para el evento', {
            useGlobalPixel, globalPixelId, cursoPixel, curso: curso?.nombre_curso,
          })
        }
        return
      }

      // Si es modo individual, inicializar el pixel del curso si es nuevo
      if (
        !useGlobalPixel &&
        cursoPixel &&
        cursoPixel.length >= 10 &&
        !initializedPixels.current.has(cursoPixel)
      ) {
        initExtraPixel(cursoPixel)
        initializedPixels.current.add(cursoPixel)
      }

      const eventData = {
        content_name: curso.nombre_curso || 'Curso',
        content_type: 'product',
        currency: 'ARS',
        ...(curso.precio && parseFloat(curso.precio) > 0
          ? { value: parseFloat(curso.precio) }
          : {})
      }

      if (useGlobalPixel) {
        window.fbq('track', 'InitiateCheckout', eventData)
      } else {
        window.fbq('trackSingle', cursoPixel, 'InitiateCheckout', eventData)
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[MetaPixel] Evento disparado:', {
          modo:      useGlobalPixel ? 'GLOBAL (pixel_global=true)' : 'INDIVIDUAL (pixel_global=false)',
          pixel:     targetPixel,
          curso:     curso.nombre_curso,
          eventData,
        })
      }
    },
    [fromAd, globalPixelId, useGlobalPixel]
  )

  return {
    globalPixelId,
    useGlobalPixel,
    pageViewActivo,   // ← nuevo: refleja campo "estado" de pixel_meta
    fromAd,
    initialized,
    fireInitiateCheckout,
  }
}